import {
  useAppDispatch,
  useAppSelector,
} from "../../../../hooks/useTypedSelector";
import {
  logout,
  sendForgotPasswordMail,
} from "../../../auth/infrastructure/slices/authSlice/authSlice";
import {
  Layout,
  LayoutContent,
  LayoutHeader,
  LayoutHeaderTitle,
} from "../../../general/modules/commons/layout/layout";
import { useTranslation } from "react-i18next";
import {
  Breadcrumb,
  BreadCrumbItem,
} from "../../../general/modules/commons/breadcrumb/breadcrumb";
import { useParams, useNavigate } from "react-router-dom";
import { UserModel as AuthUserModel } from "../../../auth/domain/models/userModel";
import { UserModel } from "../../domain/models/userModel";
import useCrudForm from "../../../../hooks/useCrudForm";
import { userFormSchema } from "../../domain/schemas/userSchema";
import { UserMutateFormTemplate } from "../commons/templates/userMutateFormTemplate";
import { Content } from "../../../general/modules/commons/content/content";
import { ContentHeader } from "../../../general/modules/commons/content/contentHeader";
import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { RequiredAsterisks } from "../../../general/modules/commons/Asterisks/requiredAsterisks";
import AsyncSelect from "react-select/async";
import { toast } from "sonner";

export interface UserMutateInputs {
  username: string;
  name: string;
  email: string | undefined;
  roles: { name: string } | { name: string }[];
  password: string | undefined;
  password_confirmation: string | undefined;
  status: boolean;
}

type ModelUserModel = Omit<UserModel, "roles"> & {
  roles: { name: string }[];
};

export const UserMutatePage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("common");
  const { id } = useParams();
  const navigate = useNavigate();

  const [sendMail, setSendMail] = useState<boolean>(false);

  const currentUser: AuthUserModel = useAppSelector((state) => state.auth.user);
  const model = useAppSelector((state) => state.crudMutate)
    .model as ModelUserModel;
  const availableRoles = currentUser.availableRoles;

  const loadedOptions = availableRoles?.map((role) => ({
    value: role,
    label: t("user.role." + role),
  }));

  const [roles, setRoles] = useState<Array<{ name: string }> | undefined>(
    model.roles || undefined,
  );
  const [isCurrentUser, setIsCurrentUser] = useState<boolean>(true);
  useEffect(() => {
    if (id !== String(currentUser.id)) {
      setIsCurrentUser(false);
    }
  }, []);

  const title = model.id
    ? t("user.mutate.edit", {
        name: model.name ? model.name : model.username,
      })
    : t("user.mutate.create");

  const crud = useCrudForm({
    schema: userFormSchema,
    prefix: "users",
    createMutation: "upsertUser",
    updateMutation: "upsertUser",
    deleteMutation: !isCurrentUser ? "deleteUser" : undefined,
    id: id ? parseInt(id) : undefined,
    editUrl: "/user/edit/:id",
    formTemplate: UserMutateFormTemplate,
    queryExclude: ["password", "password_confirmation"],
    mutationExclude: ["password_confirmation", "availableRoles"],
    onBeforeSubmit: (values) => {
      if (roles && roles.length > 0) {
        (values as UserMutateInputs).roles = roles[0] || model.roles[0];
      } else {
        (values as UserMutateInputs).roles = model.roles[0];
      }
      if (
        (values as UserMutateInputs).password ||
        (values as UserMutateInputs).password_confirmation
      ) {
        if (
          (values as UserMutateInputs).password !==
          (values as UserMutateInputs).password_confirmation
        ) {
          toast.error(t("Passwords do not match"));
          return { validationError: "Passwords do not match" };
        }
      }
      return {
        ...values,
        id: id ? parseInt(id) : undefined,
      };
    },
    onAfterSubmit: (result) => {
      if (sendMail) {
        if ((result as UserModel).email) {
          void dispatch(
            sendForgotPasswordMail({
              userString: (result as UserModel).username,
              newAccount: true,
            }),
          );
        } else {
          toast.error(t("user.mutate.noEmail"));
        }
      }
      const id = (result as UserModel).id;
      navigate(`/user/edit/${id}`);
    },
  });

  function isEmpty(obj: object) {
    return Object.keys(obj).length === 0;
  }

  if (Object.keys(model).length < 1 && id)
    return (
      <Layout>
        <LayoutContent>
          <Content>
            <ContentHeader className={"mt-3"} title={t("Access denied")} />
            <p className={"mt-3"}>{t("You don't have access to this page")}</p>
          </Content>
        </LayoutContent>
      </Layout>
    );

  return (
    <Layout
      header={
        <LayoutHeader logout={() => void dispatch(logout())}>
          <LayoutHeaderTitle title={title} />
          <Breadcrumb>
            <BreadCrumbItem to="/users">
              {t("user.mutate.breadcrumb")}
            </BreadCrumbItem>
          </Breadcrumb>
        </LayoutHeader>
      }
    >
      <LayoutContent>
        <Content md={8}>
          <ContentHeader title={title} />
          {crud.form}

          {!isCurrentUser && (
            <Form.Group className="mb-3">
              <Form.Label>
                {t("Role")} <RequiredAsterisks />
              </Form.Label>
              <AsyncSelect
                placeholder={t("Select...")}
                className={"entity-select"}
                classNamePrefix={"entity-select"}
                defaultInputValue={
                  !isEmpty(model) && model.roles.length > 0
                    ? model.roles[0].name
                    : undefined
                }
                defaultOptions={loadedOptions}
                onChange={(selectedOption) => {
                  const makeNewRole = (role: string) => {
                    const newRole: { name: string } = {
                      name: "",
                    };
                    newRole.name = role;
                    return newRole;
                  };
                  const createdRole = makeNewRole(
                    selectedOption?.value as string,
                  );
                  setRoles([createdRole]);
                }}
              />
            </Form.Group>
          )}

          {(currentUser.role === "super-admin" ||
            currentUser.role === "dbh-manager") &&
            isEmpty(model) && (
              <Form.Group className="mb-3">
                <Form.Check
                  label={t("user.send.newMail")}
                  type="checkbox"
                  checked={sendMail}
                  onChange={(e) => setSendMail(e.target.checked)}
                />
              </Form.Group>
            )}
        </Content>

        <div className="col-md-4">
          <Content md={12}>
            {crud.manageHeader}
            {crud.manage}
          </Content>
          {(currentUser.role === "super-admin" ||
            currentUser.role === "dbh-manager") &&
            !isEmpty(model) &&
            model.email && (
              <Content md={12} className={"mt-4"}>
                <ContentHeader title={t("user.mutate.actions")} />
                <Button
                  className={"btn-light mt-3"}
                  onClick={() => {
                    void dispatch(
                      sendForgotPasswordMail({
                        userString: model.email || model.username,
                      }),
                    ).then(() => {
                      toast.success(
                        t("user.reset.password.success", {
                          user: model.email || model.username,
                        }),
                      );
                    });
                  }}
                >
                  {t("user.reset.password")}
                </Button>
              </Content>
            )}
          {crud.logs}
        </div>
      </LayoutContent>
    </Layout>
  );
};
